// Third party library imports
import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, UrlTree } from '@angular/router';
import { isObservable, map } from 'rxjs';

// Zopup library imports
import { StorageKey, StorageService } from '@zopup/zop-shared';
import { TenantDto } from '@zopup/zop-types';

// Internal project imports
import { AuthGuard } from './auth.guard';
import { ZopAuthConfigService } from '../providers/zop-auth-config.provider';

export const TenantGuard: CanActivateFn | CanActivateChildFn = (route, state) =>
{
    const router: Router = inject(Router);
    const authConfig = inject(ZopAuthConfigService);

    const baseResult = AuthGuard(route, state); // Call the base guard
    const selectedTenant = inject(StorageService).getItem<TenantDto>(StorageKey.tenant);
    let isRouteAllowed = false;
    
    const getTenantSelectionUrl = () => router.parseUrl(authConfig.getConfig().routes.tenantSelection);
    
    // Handle boolean directly
    if (typeof baseResult === 'boolean' && baseResult) {
        isRouteAllowed = baseResult && (selectedTenant !== undefined && selectedTenant !== null);
        if (!isRouteAllowed) {
            return getTenantSelectionUrl();
        }
        return isRouteAllowed;
    }

    // Handle UrlTree directly
    if (baseResult instanceof UrlTree) {
        return baseResult;
    }

    // Handle Observable<boolean | UrlTree>
    if (isObservable(baseResult)) {
        return baseResult.pipe(
            map(result => {
                if (typeof result === 'boolean' && result) {
                    isRouteAllowed = result && (selectedTenant !== undefined && selectedTenant !== null);
                    if (!isRouteAllowed) {
                        return getTenantSelectionUrl();
                    }
                }
                return result; // If result is UrlTree, pass it directly
            })
        );
    }

    // Handle Promise<boolean | UrlTree>
    if (baseResult instanceof Promise) {
        return baseResult.then(result => {
            if (typeof result === 'boolean' && result) {
                isRouteAllowed = result && (selectedTenant !== undefined && selectedTenant !== null);
                if (!isRouteAllowed) {
                    return getTenantSelectionUrl();
                }
            }
            return result; // If result is UrlTree, pass it directly
        });
    }

    // Default to a safe fallback if no match
    return false;
};
